// src/components/Talleres/FormularioInscripcion/PasoPlan.jsx
import React from 'react';
import { Alert, Card, Tag, Space, Radio, Button, Divider, Spin, Tooltip } from 'antd';
import {
  ThunderboltOutlined,
  IdcardOutlined,
  UserOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';

export default function PasoPlan({
  taller,
  selectedPlan,
  setSelectedPlan,
  selectedHorarios,
  preciosDisponibles,
  loadingDescuentos,
  esVecino,
  onNext,
  onPrev
}) {
  const calcularTotal = () => {
    if (!selectedPlan) return { total: 0, subtotal: 0, matricula: 0 };
    const planData = taller?.modalidadPago?.find(p => p.plan === selectedPlan);
    if (!planData) return { total: 0, subtotal: 0, matricula: 0 };
    const valorPlan = planData.valorParticular || 0;
    const matricula = planData.matricula || 0;
    const subtotal = selectedHorarios.length * valorPlan;
    const totalMatricula = selectedHorarios.length * matricula;
    return {
      total: subtotal + totalMatricula,
      subtotal,
      totalMatricula,
      valorPorHorario: valorPlan,
      matriculaPorHorario: matricula
    };
  };

  const totales = calcularTotal();

  const tieneDescuentosDisponibles =
    preciosDisponibles &&
    Object.values(preciosDisponibles).some(
      p => p.tieneDescuentosAutomaticos || p.tienePrecioAdultoMayor || p.tienePrecioVecino
    );

  // Construye filas informativas de precios disponibles para un plan
  // No marca nada como "activo" porque la edad y vecino se verifican en pasos posteriores
  const construirFilasPrecios = (plan) => {
    const precioParticular = plan.valorParticular || 0;
    const precioAdultoMayor = plan.valorAdultoMayor || 0;
    const precioVecino = plan.valorVecino || 0;
    const filas = [];

    // Siempre: precio particular
    filas.push({
      key: 'particular',
      label: 'Particular',
      condicion: 'Sin Tarjeta Vecino',
      precio: precioParticular,
      ahorro: null,
      colorLabel: 'text-gray-700',
      colorPrecio: 'text-gray-800',
      bg: 'bg-gray-50',
      icon: <UserOutlined className="text-gray-400" />,
      tooltip: 'Precio estándar sin beneficios adicionales'
    });

    // Si existe precio vecino para menores de 60
    if (precioVecino > 0 && precioVecino < precioParticular) {
      filas.push({
        key: 'vecino',
        label: 'Vecino',
        condicion: 'Con Tarjeta Vecino · menor de 60 años',
        precio: precioVecino,
        ahorro: precioParticular - precioVecino,
        colorLabel: 'text-blue-700',
        colorPrecio: 'text-blue-700',
        bg: 'bg-blue-50',
        icon: <IdcardOutlined className="text-blue-500" />,
        tooltip: 'Aplica a personas menores de 60 años con Tarjeta Vecino vigente'
      });
    }

    // Si existe precio adulto mayor
    if (precioAdultoMayor > 0 && precioAdultoMayor < precioParticular) {
      // Con tarjeta vecino → precio adulto mayor (sin descuento extra)
      filas.push({
        key: 'adultoMayorConVecino',
        label: 'Adulto Mayor',
        condicion: 'Con Tarjeta Vecino · 60 años o más',
        precio: precioAdultoMayor,
        ahorro: precioParticular - precioAdultoMayor,
        colorLabel: 'text-purple-700',
        colorPrecio: 'text-purple-700',
        bg: 'bg-purple-50',
        icon: <UserOutlined className="text-purple-500" />,
        tooltip: 'Persona ≥ 60 años con Tarjeta Vecino. Se aplica tarifa adulto mayor como precio final'
      });

      // Sin tarjeta vecino → paga particular
      filas.push({
        key: 'adultoMayorSinVecino',
        label: 'Adulto Mayor',
        condicion: 'Sin Tarjeta Vecino · 60 años o más',
        precio: precioParticular,
        ahorro: null,
        colorLabel: 'text-gray-500',
        colorPrecio: 'text-gray-600',
        bg: 'bg-gray-50',
        icon: <UserOutlined className="text-gray-400" />,
        tooltip: 'Persona ≥ 60 años sin Tarjeta Vecino paga precio particular'
      });
    }

    return filas;
  };

  return (
    <div>
      <Alert
        message="Selecciona el plan de pago"
        description="El precio final se calculará según la persona que inscribas en el siguiente paso"
        type="info"
        showIcon
        className="mb-4"
      />

      {/* Banner tarjeta vecino detectada */}
      {esVecino && (
        <Alert
          message={
            <span className="flex items-center gap-2">
              <IdcardOutlined className="text-blue-600" />
              Eres beneficiario de Tarjeta Vecino
            </span>
          }
          description="El precio con descuento vecino se aplicará automáticamente según la edad de quien se inscriba."
          type="success"
          showIcon={false}
          className="mb-4"
        />
      )}

      {/* Banner descuentos adicionales disponibles */}
      {tieneDescuentosDisponibles && !loadingDescuentos && (
        <Alert
          message="Precios especiales disponibles en este taller"
          description={
            <div className="space-y-1 mt-1">
              {preciosDisponibles && Object.values(preciosDisponibles).some(p => p.tieneDescuentosAutomaticos) && (
                <div className="flex items-center gap-2">
                  <ThunderboltOutlined className="text-orange-500" />
                  <span>Descuentos automáticos según condiciones</span>
                </div>
              )}
              {preciosDisponibles && Object.values(preciosDisponibles).some(p => p.tienePrecioAdultoMayor) && (
                <div className="flex items-center gap-2">
                  <UserOutlined className="text-purple-500" />
                  <span>Tarifa especial para adultos mayores (60+ años)</span>
                </div>
              )}
              {preciosDisponibles && Object.values(preciosDisponibles).some(p => p.tienePrecioVecino) && (
                <div className="flex items-center gap-2">
                  <IdcardOutlined className="text-blue-500" />
                  <span>Tarifa especial para beneficiarios de Tarjeta Vecino</span>
                </div>
              )}
            </div>
          }
          type="warning"
          showIcon
          className="mb-4"
        />
      )}

      {loadingDescuentos && (
        <div className="text-center py-4">
          <Spin tip="Cargando información de descuentos..." />
        </div>
      )}

      <Radio.Group
        value={selectedPlan}
        onChange={(e) => setSelectedPlan(e.target.value)}
        className="w-full"
      >
        <div className="space-y-3">
          {taller.modalidadPago?.map((plan) => {
            const precioParticular = plan.valorParticular || 0;
            const precioAdultoMayor = plan.valorAdultoMayor || 0;
            const precioVecino = plan.valorVecino || 0;
            const matricula = plan.matricula || 0;
            const infoDisponible = preciosDisponibles?.[plan.plan] || {};
            const filasPrecios = construirFilasPrecios(plan);
            const tieneMultiplesPrecios = filasPrecios.length > 1;

            return (
              <Card
                key={plan._id || plan.plan}
                size="small"
                className={`cursor-pointer transition-all ${
                  selectedPlan === plan.plan
                    ? 'border-2 border-blue-500 bg-blue-50'
                    : 'hover:border-blue-300'
                }`}
                onClick={() => setSelectedPlan(plan.plan)}
              >
                {/* Cabecera */}
                <div className="flex items-center justify-between">
                  <Space align="start">
                    <Radio value={plan.plan} />
                    <div>
                      <div className="font-semibold capitalize flex items-center gap-2 flex-wrap">
                        {plan.plan}
                        {infoDisponible.tieneDescuentosAutomaticos && (
                          <Tag color="orange" icon={<ThunderboltOutlined />} className="text-xs">
                            DCTO AUTO
                          </Tag>
                        )}
                        {precioVecino > 0 && precioVecino < precioParticular && (
                          <Tag color="blue" icon={<IdcardOutlined />} className="text-xs">
                            DCTO VECINO
                          </Tag>
                        )}
                        {precioAdultoMayor > 0 && precioAdultoMayor < precioParticular && (
                          <Tag color="purple" icon={<UserOutlined />} className="text-xs">
                            PRECIO +60
                          </Tag>
                        )}
                      </div>
                      {matricula > 0 && (
                        <div className="text-xs text-gray-500 mt-1">
                          + Matrícula: ${matricula.toLocaleString('es-CL')}
                        </div>
                      )}
                    </div>
                  </Space>

                  <div className="text-right">
                    <div className="text-xl font-bold text-blue-600">
                      ${precioParticular.toLocaleString('es-CL')}
                    </div>
                    <div className="text-xs text-gray-400">precio base / horario</div>
                  </div>
                </div>

                {/* Tabla de precios informativos — sin marcar "activo" */}
                {tieneMultiplesPrecios && (
                  <>
                    <Divider className="my-3" />
                    <div className="text-xs text-gray-500 font-semibold mb-2 flex items-center gap-1">
                      <InfoCircleOutlined />
                      Tarifas disponibles según perfil del inscrito:
                    </div>
                    <div className="space-y-1">
                      {filasPrecios.map((fila) => (
                        <Tooltip key={fila.key} title={fila.tooltip}>
                          <div
                            className={`flex items-center justify-between rounded px-3 py-2 ${fila.bg}`}
                          >
                            <div className="flex items-center gap-2">
                              {fila.icon}
                              <div>
                                <span className={`text-xs font-semibold ${fila.colorLabel}`}>
                                  {fila.label}
                                </span>
                                <span className="text-xs text-gray-400 ml-1">
                                  — {fila.condicion}
                                </span>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className={`text-sm font-bold ${fila.colorPrecio}`}>
                                ${fila.precio.toLocaleString('es-CL')}
                              </span>
                              {fila.ahorro && (
                                <span className="text-xs text-blue-600 bg-blue-100 rounded px-1">
                                  −${fila.ahorro.toLocaleString('es-CL')}
                                </span>
                              )}
                            </div>
                          </div>
                        </Tooltip>
                      ))}
                    </div>
                    <p className="text-xs text-gray-400 mt-2 flex items-center gap-1">
                      <InfoCircleOutlined />
                      La tarifa exacta se determina en el siguiente paso según quién se inscriba
                    </p>
                  </>
                )}
              </Card>
            );
          })}
        </div>
      </Radio.Group>

      {/* Resumen total estimado */}
      {selectedPlan && totales.total > 0 && (
        <Card className="mt-4 bg-blue-50 border-blue-200">
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="font-semibold">Total estimado (precio base):</span>
              <span className="text-2xl font-bold text-blue-600">
                ${totales.total.toLocaleString('es-CL')}
              </span>
            </div>
            <Divider className="my-2" />
            <div className="text-sm space-y-1">
              <div className="flex justify-between text-gray-600">
                <span>
                  {selectedHorarios.length} horario(s) × ${totales.valorPorHorario.toLocaleString('es-CL')}
                </span>
                <span>${totales.subtotal.toLocaleString('es-CL')}</span>
              </div>
              {totales.totalMatricula > 0 && (
                <div className="flex justify-between text-gray-600">
                  <span>
                    {selectedHorarios.length} matrícula(s) × ${totales.matriculaPorHorario.toLocaleString('es-CL')}
                  </span>
                  <span>${totales.totalMatricula.toLocaleString('es-CL')}</span>
                </div>
              )}
            </div>
            <Alert
              message="💡 Precio final"
              description="El descuento definitivo se aplicará en el siguiente paso según la persona inscrita"
              type="info"
              showIcon
              className="mt-3"
              banner
            />
          </div>
        </Card>
      )}

      <Divider />
      <div className="flex justify-between">
        <Button onClick={onPrev} size="large">Anterior</Button>
        <Button
          type="primary"
          size="large"
          onClick={onNext}
          disabled={!selectedPlan}
        >
          Siguiente
        </Button>
      </div>
    </div>
  );
}
